<template>
  <div class="outpatient-charge-container">
    <!-- 时间筛选 - 固定区域 -->
    <div class="filter-section">
      <el-card class="filter-card">
        <el-form :model="queryParams" inline>
          <el-form-item label="统计时间">
            <el-date-picker
                v-model="timeRange"
                type="datetimerange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                value-format="yyyy-MM-dd"
                @change="handleTimeChange"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="loadData" :loading="loading">
              查询
            </el-button>
            <el-button @click="resetQuery">
              重置
            </el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </div>

    <!-- 内容区域 - 可滚动 -->
    <div class="content-section">
      <!-- 总体统计卡片 -->
      <el-row :gutter="0" class="stats-row">
        <el-col :xs="12" :sm="6" :md="6" :lg="6">
          <el-card class="stat-card income-card" shadow="hover">
            <div class="stat-content">
              <div class="stat-icon cash-icon">
                <i class="el-icon-money"></i>
              </div>
              <div class="stat-info">
                <div class="stat-title">现金金额</div>
                <div class="stat-value">¥{{ formatAmount(statsData.cashAmt) }}</div>
                <div class="stat-desc">现金支付总额</div>
              </div>
            </div>
          </el-card>
        </el-col>

        <el-col :xs="12" :sm="6" :md="6" :lg="6">
          <el-card class="stat-card paid-card" shadow="hover">
            <div class="stat-content">
              <div class="stat-icon paid-icon">
                <i class="el-icon-credit-card"></i>
              </div>
              <div class="stat-info">
                <div class="stat-title">实付金额</div>
                <div class="stat-value">¥{{ formatAmount(statsData.paidAmt) }}</div>
                <div class="stat-desc">患者实际支付</div>
              </div>
            </div>
          </el-card>
        </el-col>

        <el-col :xs="12" :sm="6" :md="6" :lg="6">
          <el-card class="stat-card cost-card" shadow="hover">
            <div class="stat-content">
              <div class="stat-icon cost-icon">
                <i class="el-icon-trend-charts"></i>
              </div>
              <div class="stat-info">
                <div class="stat-title">成本金额</div>
                <div class="stat-value">¥{{ formatAmount(statsData.costAmt) }}</div>
                <div class="stat-desc">医疗成本总额</div>
              </div>
            </div>
          </el-card>
        </el-col>

        <el-col :xs="12" :sm="6" :md="6" :lg="6">
          <el-card class="stat-card discount-card" shadow="hover">
            <div class="stat-content">
              <div class="stat-icon discount-icon">
                <i class="el-icon-discount"></i>
              </div>
              <div class="stat-info">
                <div class="stat-title">优惠金额</div>
                <div class="stat-value">¥{{ formatAmount(statsData.distAmt) }}</div>
                <div class="stat-desc">优惠减免总额</div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <!-- 聚合支付卡片 -->
      <el-card class="group-card" shadow="hover" v-if="statsData.groupAmt > 0">
        <div slot="header" class="card-header">
          <span class="card-title">
            <i class="el-icon-collection"></i>
            聚合支付
          </span>
        </div>
        <div class="group-content">
          <div class="group-amount">¥{{ formatAmount(statsData.groupAmt) }}</div>
          <div class="group-desc">聚合支付金额</div>
        </div>
      </el-card>

      <!-- 各费用类型明细统计 - 独立标签页 -->
      <el-tabs v-model="activeTab1" type="border-card" class="separate-tab">
        <el-tab-pane label="各费用类型明细统计" name="detail">
          <el-table
              :data="statsData.detailStaticsRespVOS"
              border
              stripe
              @row-dblclick="handleDbClickTypeDetail"
              v-loading="loading"
              empty-text="暂无数据"
              class="detail-table"
              style="width: 100%"
          >
            <el-table-column type="index" label="序号" align="center"/>

            <el-table-column prop="feeType" label="费用类型" align="center">
              <template slot-scope="{ row }">
                <el-tag :type="getFeeTypeTagType(row.feeType)" class="fee-type-tag">
                  {{ getFeeTypeName(row.feeType) }}
                </el-tag>
              </template>
            </el-table-column>

            <el-table-column prop="costAmt" label="成本金额" align="center">
              <template slot-scope="{ row }">
                <span class="amount-text cost-text">
                  ¥{{ formatAmount(row.costAmt) }}
                </span>
              </template>
            </el-table-column>

            <el-table-column prop="paidAmt" label="实收金额" align="center">
              <template slot-scope="{ row }">
                <span class="amount-text paid-text">
                  ¥{{ formatAmount(row.paidAmt) }}
                </span>
              </template>
            </el-table-column>

            <el-table-column label="毛利" align="center">
              <template slot-scope="{ row }">
                <span :class="getProfitRateClass(row)" class="profit-rate">
                  {{ calculateProfitRate(row) }}
                </span>
              </template>
            </el-table-column>

            <el-table-column label="占比" align="center">
              <template slot-scope="{ row }">
                <div class="percentage-container">
                  <el-progress
                      :percentage="Number(calculatePercentage(row))"
                      :show-text="false"
                      :color="getFeeTypeColor(row.feeType)"
                      class="percentage-bar"
                  />
                  <span class="percentage-text">
                    {{ calculatePercentage(row) }}%
                  </span>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>

      <!-- 统计摘要 - 独立标签页 -->
      <el-tabs v-model="activeTab2" type="border-card" class="separate-tab">
        <el-tab-pane label="统计摘要" name="summary">
          <el-descriptions :column="2" border class="summary-descriptions">
            <el-descriptions-item label="统计时间">
              {{ queryParams.startTime }} 至 {{ queryParams.endTime }}
            </el-descriptions-item>
            <el-descriptions-item label="费用类型数量">
              <el-tag size="small">{{ statsData.detailStaticsRespVOS?.length || 0 }} 种</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="总成本金额">
              <el-tag type="info" class="amount-tag">¥{{ formatAmount(totalCost) }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="总实收金额">
              <el-tag type="success" class="amount-tag">¥{{ formatAmount(totalPaid) }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="现金金额">
              <el-tag type="warning">¥{{ formatAmount(statsData.cashAmt) }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="优惠金额">
              <el-tag type="danger">¥{{ formatAmount(statsData.distAmt) }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="聚合支付金额" v-if="statsData.groupAmt > 0">
              <el-tag type="primary">¥{{ formatAmount(statsData.groupAmt) }}</el-tag>
            </el-descriptions-item>
          </el-descriptions>
        </el-tab-pane>
      </el-tabs>
    </div>
    <el-dialog
        :close-on-click-modal="false"
        title="西药收费详情"
        :visible.sync="westChargesDetailDiagnosisModalVisible"
        :before-close="handleWestChargesDetail"
        width="70%"
        style="height: 90vh"
        class="patient-dialog"
    >
      <!--
            @row-dblclick="addToWesternSelectedTable"
      -->
      <el-form :inline="true" :model="queryConditionForm" class="query-form" size="mini">
        <el-form-item label="就诊时间:">
          <el-date-picker
              size="mini"
              v-model="queryConditionForm.timeFrameArr"
              type="datetimerange"
              align="right"
              unlink-panels
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              @change="handleDbClickTypeDetail(currentDbData)"
          >
          </el-date-picker>
        </el-form-item>
        <el-form-item label="冲销状态:">
          <el-select
              v-model="queryConditionForm.nuyState"
              placeholder="请选择冲销状态"
              size="mini"
              clearable
              @change="handleDbClickTypeDetail(currentDbData)"
          >
            <el-option
                v-for="item in nuyStateOptions"
                :key="item.id"
                :label="item.label"
                :value="item.id"
            >
            </el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <el-table
          ref="westernTable"
          sizi="mini"
          :data="westChargesDetailData"
          border
          highlight-current-row
          style="width: 100%;height: 100%"
          show-summary
          @row-dblclick="handleDbClickWestDetail"
          :summary-method="getSummariesWestChargesDetail"
          class="compact-mini-table"
          :max-height="500"
      >
        <el-table-column
            v-for="(col, inx) in westChargesDetailRows"
            :key="`col_${col.prop}_${col.type}`"
            :type="col.type"
            :prop="col.prop"
            :label="col.name"

            :render-header="labelHead"
            align="center"
            :min-width="getColumnWidth(col.label,col.prop,westChargesDetailData)"
        >
          <template #default="{ row }">
            {{ safeGetMappedValue(col, row) }}
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
          class="fixed-pagination"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pageNum"
          :page-sizes="[8, 16, 24]"
          :page-size="pageSize"
          :total="totalSize"
          layout="total, sizes, prev, pager, next, jumper"
          background
      >
      </el-pagination>
      <div slot="footer" class="dialog-footer no-line">
        <el-button @click="handleWestChargesDetail" size="small">取消</el-button>
      </div>
    </el-dialog>

    <el-dialog
        :close-on-click-modal="false"
        title="西药药品详情"
        :visible.sync="westModalVisible"
        :before-close="handleWestDetail"
        width="70%"
        class="patient-dialog"
    >
      <el-form :inline="true" :model="westPatientData" class="query-form" size="mini">
        <el-form-item label="姓名:">
          <el-input
              v-model="westPatientData.name"
              size="mini"
              :disabled="true"
              placeholder="姓名"
          ></el-input>
        </el-form-item>

        <el-form-item label="年龄:">
          <el-input
              v-model="westPatientData.age"
              size="mini"
              :disabled="true"
              placeholder="年龄"
          ></el-input>
        </el-form-item>

        <el-form-item label="性别:">
          <el-select
              v-model="westPatientData.sex"
              size="mini"
              :disabled="true"
              placeholder="性别"
          >
            <el-option
                v-for="item in sexOptions"
                :key="item.id"
                :label="item.label"
                :value="item.id"
            >
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="划价方式:">
          <el-select
              v-model="westPatientData.pricingType"
              size="mini"
              :disabled="true"
              placeholder="划价方式"
          >
            <el-option
                v-for="item in pricingTypeOptions"
                :key="item.id"
                :label="item.label"
                :value="item.id"
            >
            </el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <!--
            @row-dblclick="addToWesternSelectedTable"
      -->
      <el-table
          ref="westTable"
          sizi="mini"
          :data="westData"
          border
          highlight-current-row
          style="width: 100%;height: 100%"
          show-summary
          :summary-method="getSummariesWest"
          class="compact-mini-table"
      >
        <el-table-column
            v-for="(col, inx) in westTableRows"
            :key="`col_${col.prop}_${col.type}`"
            :type="col.type"
            :prop="col.prop"
            :label="col.name"
            :render-header="labelHead"
            align="center"
            :min-width="getColumnWidth(col.label, col.prop, westData)"
        >
          <template #default="{ row }">
            {{ safeGetMappedValue(col, row) }}
          </template>
        </el-table-column>
      </el-table>
      <div slot="footer" class="dialog-footer no-line">
        <el-button @click="handleWestDetail" size="small">取消</el-button>
      </div>
    </el-dialog>

    <el-dialog
        :close-on-click-modal="false"
        title="处置项目详情"
        :visible.sync="disposeDetialModalVisible"
        :before-close="handleDisposeDetailModalVisible"
        width="70%"
        class="patient-dialog"
    >
      <el-form :inline="true" :model="queryConditionForm" class="query-form" size="mini">
        <el-form-item label="就诊时间:">
          <el-date-picker
              size="mini"
              v-model="queryConditionForm.timeFrameArr"
              type="datetimerange"
              align="right"
              unlink-panels
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              @change="handleDbClickTypeDetail(currentDbData)"
          >
          </el-date-picker>
        </el-form-item>
        <el-form-item label="收费状态:">
          <el-select
              v-model="queryConditionForm.chargeStatus"
              placeholder="请选择收费状态"
              size="mini"
              clearable
              @change="handleDbClickTypeDetail(currentDbData)"
          >
            <el-option
                v-for="item in chargeStatusOptions"
                :key="item.id"
                :label="item.label"
                :value="item.id"
            >
            </el-option>
          </el-select>
        </el-form-item>
        <!--        chargeStatus-->
      </el-form>
      <!--
            @row-dblclick="addToWesternSelectedTable"
      -->
      <el-table
          ref="westTable"
          sizi="mini"
          :data="disposeData"
          border
          highlight-current-row
          style="width: 100%;height: 100%"
          show-summary
          :summary-method="getSummariesDispose"
          class="compact-mini-table"
      >
        <el-table-column
            v-for="(col, inx) in disposeTableRows"
            :key="`col_${col.prop}_${col.type}`"
            :type="col.type"
            :prop="col.prop"
            :label="col.name"
            :render-header="labelHead"
            align="center"
            :min-width="getColumnWidth(col.label, col.prop, disposeData)"
        >
          <template #default="{ row }">
            {{ safeGetMappedValue(col, row) }}
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
          class="fixed-pagination"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pageNum"
          :page-sizes="[8, 16, 24]"
          :page-size="pageSize"
          :total="totalSize"
          layout="total, sizes, prev, pager, next, jumper"
          background
      >
      </el-pagination>
      <div slot="footer" class="dialog-footer no-line">
        <el-button @click="handleDisposeDetailModalVisible" size="small">取消</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getChargesDetialPage,
  getOutpatientChargeStatistics
} from "@/api/outpatientChargeStatistics/outpatientChargeStatistics";
import dayjs from "dayjs";
import {
  getDoctordisposeOneById,
  getDoctordisposePage,
  getDrxMainRefundList
} from "@/api/reservation/outpatientChargeList";
import {getWestDetail} from "@/api/reservation/outpatientCharge";

export default {
  name: 'OutpatientChargeReport',
  data() {
    return {
      dosaUntOptions: [],//单位
      medicationFrequencyOptions: [],
      queryConditionForm: {
        timeFrameArr: [
          dayjs().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
          dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss')
        ],
        nuyState: 0,
        chargeStatus: 1,
      },
      currentDbData: null,
      westChargesDetailDiagnosisModalVisible: false,
      westModalVisible: false,
      disposeDetialModalVisible: false,
      westChargesDetailData: [],
      westData: [],
      disposeData: [],
      westPatientData: {
        name: '',
        age: '',
        sex: '',
        pricingType: ''
      },

      disposePatientData: {
        name: '',
        age: '',
        sex: '',
        pricingType: ''
      },
      sexOptions: [{id: 1, label: '男'}, {id: 2, label: '女'}],
      pricingTypeOptions: [{id: 1, label: '医嘱划价'}, {id: 2, label: '手工划价'}, {id: 3, label: '互联网划价'}],
      chargeStatusOptions: [{id: 0, label: '未收费'}, {id: 1, label: '已收费'}, {id: 2, label: '已申请退费'}, {
        id: 3,
        label: '已退费'
      }],
      dataSourceOptions: [{id: 1, label: '医嘱'}, {id: 2, label: '护士'}, {id: 3, label: '检查'}, {
        id: 4,
        label: '检验'
      }, {id: 5, label: '其他'}],
      westChargesDetailRows: [
        {name: "业务来源", prop: "busForm", type: "", op: 'busFormOptions'},
        {name: "开单科室", prop: "bilgDepName", type: ""},
        {name: "开单医生", prop: "bilgDrName", type: ""},
        {name: "实际金额", prop: "paidAmt", type: ""},
        {name: "实际价格", prop: "paidPric", type: ""},
        {name: "冲销状态", prop: "nuyState", type: "", op: 'nuyStateOptions'},
        {name: "冲销金额", prop: "nuyAmt", type: ""},
        {name: "冲销数量", prop: "nuyCnt", type: ""},
      ],
      westTableRows: [
        {name: "药品名称", prop: "drugName"},
        {name: "规格描述", prop: "drugSpec"},
        {name: "数量", prop: "cntStr"},
        {name: "给药方式", prop: "medcWayId", op: 'medicationFrequencyOptions'},
        {name: "成本金额", prop: "purcAmt"},
        {name: "销售金额", prop: "saleAmt"},],

      disposeTableRows: [
        {name: "系统序号", prop: "id"},
        {name: "处置名称", prop: "costname"},
        {name: "处置类型", prop: "dataSource", op: 'dataSourceOptions'},
        {name: "收费状态", prop: "chargeStatus", op: 'chargeStatusOptions'},
        {name: "频率数量", prop: "frquCnt"},
        {name: "执行单价", prop: "executionPrice"},
        {name: "执行金额", prop: "executionAmount"},
        {name: "执行频率", prop: "exeFrquId", op: 'medicationFrequencyOptions'},
        {name: "数量", prop: "num"},
        {name: "申请退数量", prop: "applyNum"},
      ],
      busFormOptions: [{id: 0, label: '医嘱'}, {id: 1, label: '手工'}, {id: 2, label: '护理'}, {id: 3, label: '网络'},],
      nuyStateOptions: [{id: 0, label: '否'}, {id: 1, label: '冲销原单'}, {id: 2, label: '冲销负单'}],
      pageNum: 1, //当前页currentPage
      pageSize: 8,
      totalSize: 10, //总条数
      loading: false,
      activeTab1: 'detail',
      activeTab2: 'summary',
      statsData: {
        cashAmt: 0,
        costAmt: 0,
        detailStaticsRespVOS: [],
        distAmt: 0,
        groupAmt: 0,
        paidAmt: 0
      },
      timeRange: [
        dayjs().startOf('month').format('YYYY-MM-DD'),
        dayjs().format('YYYY-MM-DD')
      ],
      queryParams: {
        startTime: dayjs().startOf('month').format('YYYY-MM-DD'),
        endTime: dayjs().format('YYYY-MM-DD')
      }
    }
  },
  computed: {
    totalCost() {
      return this.statsData.detailStaticsRespVOS?.reduce((sum, item) => sum + item.costAmt, 0)
    },
    totalPaid() {
      return this.statsData.detailStaticsRespVOS?.reduce((sum, item) => sum + item.paidAmt, 0)
    }
  },
  mounted() {
    this.dosaUntOptions = this.$store.getters['preload/getDosaUntListOption'];
    console.log(this.dosaUntOptions, 'dosaUntOptionsdosaUntOptions')
    this.medicationFrequencyOptions = this.$store.getters['preload/getMedicationFrequencyOptions'];
    console.log(this.medicationFrequencyOptions, 'medicationFrequencyOptionsmedicationFrequencyOptionsmedicationFrequencyOptions')
    this.loadData()
  },
  methods: {
    async handleDbClickWestDetail(row) {
      const res = await getWestDetail(row.busId);
      if (res.code === 200) {
        this.westPatientData = res.data;
        this.westData = res.data.drxDetails;
        this.westModalVisible = true;
      } else {
        this.$message.error(res.message);
      }
    },
    getSummariesWestChargesDetail(param) {
      const {columns, data} = param;
      const sums = [];

      // 需要计算的字段配置
      const calcFields = {
        'nuyAmt': '冲销金额',
        'paidAmt': '实际金额',
        'paidPric': '实际价格',
      };

      columns.forEach((column, index) => {
        // 第一列显示行号（留空）
        if (index === 0) {
          sums[index] = '';
          return;
        }

        // 第二列（剂量单位）显示"合计"文字
        if (index === 1) {
          sums[index] = '合计';
          return;
        }

        // 只处理需要计算的字段
        if (calcFields[column.property]) {
          const total = data.reduce((sum, item) => {
            const value = parseFloat(item[column.property]) || 0;
            return sum + value;
          }, 0);

          // 如果是cnt列，显示为整数，否则保留两位小数
          sums[index] = column.property === 'cnt'
              ? Math.round(total).toString()
              : total.toFixed(2);
        } else {
          // 其他列显示占位符（与图片中的"一"一致）
          sums[index] = '';
        }
      });

      return sums;
    },
    getSummariesWest(param) {
      const {columns, data} = param;
      const sums = [];

      // 需要计算的字段配置
      const calcFields = {
        'purcAmt': '成本金额',
        'saleAmt': '销售金额',
        'cnt': '数量',
      };

      columns.forEach((column, index) => {
        // 第一列显示行号（留空）
        if (index === 0) {
          sums[index] = '';
          return;
        }

        // 第二列（剂量单位）显示"合计"文字
        if (index === 1) {
          sums[index] = '合计';
          return;
        }

        // 只处理需要计算的字段
        if (calcFields[column.property]) {
          const total = data.reduce((sum, item) => {
            const value = parseFloat(item[column.property]) || 0;
            return sum + value;
          }, 0);

          // 如果是cnt列，显示为整数，否则保留两位小数
          sums[index] = column.property === 'cnt'
              ? Math.round(total).toString()
              : total.toFixed(2);
        } else {
          // 其他列显示占位符（与图片中的"一"一致）
          sums[index] = '';
        }
      });

      return sums;
    },

    getSummariesDispose(param) {
      const {columns, data} = param;
      const sums = [];

      console.log(data, 'datadatadata')

      // 过滤出需要参与计算的数据（chargeStatus === 1）
      const validData = data.filter(item => item.chargeStatus === 1);

      // 如果没有符合条件的数据，返回空合计行
      if (validData.length === 0) {
        return new Array(columns.length).fill('');
      }

      // 需要计算的字段配置
      const calcFields = {
        'executionPrice': '执行单价',
        'executionAmount': '执行金额',
        'cnt': '数量',
      };

      columns.forEach((column, index) => {
        // 第一列显示行号（留空）
        if (index === 0) {
          sums[index] = '';
          return;
        }

        // 第二列（剂量单位）显示"合计"文字
        if (index === 1) {
          sums[index] = '收费合计';
          return;
        }

        // 只处理需要计算的字段
        if (calcFields[column.property]) {
          const total = validData.reduce((sum, item) => {
            const value = parseFloat(item[column.property]) || 0;
            return sum + value;
          }, 0);

          // 如果是cnt列，显示为整数，否则保留两位小数
          sums[index] = column.property === 'cnt'
              ? Math.round(total).toString()
              : total.toFixed(2);
        } else {
          // 其他列显示占位符（与图片中的"一"一致）
          sums[index] = '';
        }
      });

      return sums;
    },
    safeGetMappedValue(col, row) {
      // 1. 检查数据有效性
      const rawValue = row[col.prop];
      if (rawValue === null || rawValue === undefined) return ' ';

      // 2. 检查映射数组
      const mapArray = this[col.op];
      if (!col.op || !Array.isArray(mapArray)) return rawValue;

      // 3. 执行映射
      const matched = mapArray.find(item => item.id === rawValue);
      return matched?.label || rawValue;
    },

    handleSizeChange(val) {
      this.pageSize = val;
      this.pageNum = 1;
      this.handleDbClickTypeDetail(this.currentDbData);
    }
    ,
    //当前条数改变触发
    handleCurrentChange(val) {
      this.pageNum = val;
      this.handleDbClickTypeDetail(this.currentDbData);
    },
    async handleDbClickTypeDetail(row) {
      this.currentDbData = JSON.parse(JSON.stringify(row));
      let res;
      switch (row.feeType) {
        case 0:
          res = await getDoctordisposePage({
            pageNum: this.pageNum,
            pageSize: this.pageSize,
            startTime: this.queryConditionForm.timeFrameArr?.[0] || '',
            endTime: this.queryConditionForm.timeFrameArr?.[1] || '',
            chargeStatus: this.queryConditionForm.chargeStatus ?? null
          })
          if (res.code === 200) {
            this.disposeData = res.data.content;
            this.totalSize = res.data.totalSize;
            this.disposeDetialModalVisible = true;
          }
          break;

        case 1:
          res = await getChargesDetialPage({
            feeType: 1,
            pageNum: this.pageNum,
            pageSize: this.pageSize,
            startTime: this.queryConditionForm.timeFrameArr?.[0] || '',
            endTime: this.queryConditionForm.timeFrameArr?.[1] || '',
            nuyState: this.queryConditionForm.nuyState ?? null,
          });
          if (res.code === 200) {
            this.westChargesDetailData = res.data.content;
            this.totalSize = res.data.totalSize;
            this.westChargesDetailDiagnosisModalVisible = true;
          }

          break;

      }
    },
    getColumnWidth(label, prop, tableData) {
      const minWidth = 80;
      const padding = 30;

      // 检查数据是否已加载
      if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
        return minWidth; // 返回默认宽度
      }

      const contentList = tableData.map(item => item?.[prop] ?? '');

      const widths = [...contentList, label].map(text => {
        const str = String(text || '');
        return this.getTextWidth(str) + padding;
      });

      return Math.max(minWidth, ...widths);
    },

    // 精确计算文本宽度（使用Canvas）
    getTextWidth(text, fontSize = 14) {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.font = `${fontSize}px Microsoft YaHei`;
      return ctx.measureText(text).width;
    },

    labelHead(h, {column, $index}) {
      let l = column.label.length;
      let f = 12;
      column.minWidth = f * (l + 5); //加上一个文字长度
      return h("div", {class: "table-head", style: {width: "100%"}}, [
        column.label,
      ]);
    },
    handleWestChargesDetail() {
      this.pageNum = 1;
      this.pageSize = 8;
      this.totalSize = 10;
      this.queryConditionForm = Object.assign({}, this.$options.data.call(this).queryConditionForm);
      this.westChargesDetailDiagnosisModalVisible = false;
    },

    handleWestDetail() {
      this.westModalVisible = false;
    },

    handleDisposeDetailModalVisible() {
      this.pageNum = 1;
      this.pageSize = 8;
      this.totalSize = 10;
      this.queryConditionForm = Object.assign({}, this.$options.data.call(this).queryConditionForm);
      this.disposeDetialModalVisible = false;
    },
    handleTimeChange(range) {
      if (range) {
        this.queryParams.startTime = range[0]
        this.queryParams.endTime = range[1]
      }
      this.loadData();
    },

    resetQuery() {
      this.timeRange = [
        dayjs().startOf('month').format('YYYY-MM-DD'),
        dayjs().format('YYYY-MM-DD')
      ]
      this.queryParams.startTime = dayjs().startOf('month').format('YYYY-MM-DD')
      this.queryParams.endTime = dayjs().format('YYYY-MM-DD')
      this.loadData()
    },

    formatAmount(amount) {
      return amount?.toFixed(2);
    },

    getFeeTypeName(type) {
      const feeTypes = {
        0: '处置项目',
        1: '西药',
        2: '中药',
        3: '检查',
        4: '检验',
        5: '挂号费',
        6: '关联处置'
      }
      return feeTypes[type] || '未知类型'
    },

    getFeeTypeTagType(type) {
      const types = {
        0: '',
        1: 'success',
        2: 'warning',
        3: 'info',
        4: '',
        5: 'danger',
        6: 'info'
      }
      return types[type] || ''
    },

    getFeeTypeColor(type) {
      const colors = {
        0: '#409EFF',
        1: '#67C23A',
        2: '#E6A23C',
        3: '#909399',
        4: '#409EFF',
        5: '#F56C6C',
        6: '#909399'
      }
      return colors[type] || '#409EFF'
    },

    calculateProfitRate(row) {
      console.log(row, 'rowrowrow')
      return (row.paidAmt - row.costAmt).toFixed(2)
    },

    getProfitRateClass(row) {
      const rate = this.calculateProfitRate(row)
      if (rate > 20) return 'profit-high'
      if (rate > 10) return 'profit-medium'
      if (rate > 0) return 'profit-low'
      return 'profit-negative'
    },

    calculatePercentage(row) {
      if (this.totalPaid === 0) return 0
      return parseFloat(((row.paidAmt / this.totalPaid) * 100).toFixed(1))
    },

    async loadData() {
      try {
        const res = await getOutpatientChargeStatistics(this.queryParams)
        this.statsData = {...res.data}
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$message.error('加载数据失败')
      }
    }
  }
}
</script>

<style lang="less" scoped>

/deep/ .el-dialog__title {
  font-size: 14px;
  color: #fff;
}

// 穿透
/deep/ .el-dialog__header {
  padding: 12px;
  background-color: #007bb0;
}

.patient-dialog {
  .el-dialog {
    /* 改为动态高度控制 */
    max-height: 70vh !important;
    height: auto !important; /* 关键修改 */
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 15% !important; /* 上移对话框位置 */
    left: 50%;
    transform: translateX(-50%);
    margin: 0 !important;

    .el-dialog__header {
      height: 35px !important;
      padding: 5px 15px !important;
      flex-shrink: 0; /* 防止头部被压缩 */

      .el-dialog__title {
        font-size: 12px !important;
        line-height: 25px !important;
      }
    }

    .el-dialog__body {
      flex: 1;
      overflow: hidden; /* 外层隐藏滚动 */
      display: flex;
      flex-direction: column;
      min-height: 0; /* 关键修复：允许flex子项收缩 */

      .aform, .bform {
        flex: 1;
        overflow-y: auto; /* 只在表单容器内滚动 */
        margin-bottom: 5px;
        min-height: 0; /* 关键修复 */

        .el-tabs {
          height: 100%;
          display: flex;
          flex-direction: column;
          min-height: 0; /* 关键修复 */

          .el-tabs__content {
            flex: 1;
            overflow: visible; /* 禁用嵌套滚动 */
            padding: 5px;
          }
        }
      }
    }

    .el-dialog__footer {
      height: 40px !important;
      padding: 5px !important;
      flex-shrink: 0; /* 防止底部被压缩 */

      .el-button {
        padding: 7px 15px !important;
      }
    }
  }

  /* 小屏幕适配 */
  @media (max-height: 600px) {
    .el-dialog {
      max-height: 80vh !important;
      top: 10% !important;

      .el-dialog__body {
        padding: 5px !important;
      }
    }
  }
}

.outpatient-charge-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f5f7fa;
}

/* 筛选区域 - 固定高度，不参与滚动 */
.filter-section {
  flex-shrink: 0;
  margin-bottom: 16px;
}

.filter-card {
  width: 100%;
}

/* 内容区域 - 可滚动 */
.content-section {
  flex: 1;
  min-height: 0; /* 重要：允许内容区域收缩 */
  overflow-y: auto; /* 垂直滚动 */
  display: flex;
  flex-direction: column;
  gap: 16px; /* 元素间距 */
}

/* 统计卡片行 */
.stats-row {
  margin: 0;
}

/* 标签页样式调整 */
.separate-tab {
  flex-shrink: 0; /* 防止标签页被压缩 */
}

/* 表格样式 */
.detail-table {
  width: 100%;
}

/* 统计卡片样式 */
.stat-card {
  height: 120px;
}

.stat-content {
  display: flex;
  align-items: center;
  height: 100%;
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  font-size: 24px;
  color: white;
}

.cash-icon {
  background: #67C23A;
}

.paid-icon {
  background: #409EFF;
}

.cost-icon {
  background: #E6A23C;
}

.discount-icon {
  background: #F56C6C;
}

.stat-info {
  flex: 1;
}

.stat-title {
  font-size: 14px;
  color: #909399;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 4px;
}

.stat-desc {
  font-size: 12px;
  color: #C0C4CC;
}

/* 聚合支付卡片 */
.group-card {
  margin: 0;
}

.card-header {
  display: flex;
  align-items: center;
}

.card-title {
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.group-content {
  text-align: center;
  padding: 20px;
}

.group-amount {
  font-size: 28px;
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 8px;
}

.group-desc {
  color: #909399;
  font-size: 14px;
}
</style>